import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { LayoutGrid } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar'
import { founderAsksService, FounderAskThread, FounderAskAuthor } from '../../lib/founderAsks'
import {
  buildFounderRows,
  buildParticipantRows,
  paneMode,
  FounderRow,
  ParticipantRow,
} from './contextPaneHelpers'

const MAROON = '#57241F'
const FOUNDERS_TINT = '#FBF8F1'
const HAIRLINE = '#EAE3D5'
const VIRTUALIZE_AFTER = 50

function displayName(u: FounderAskAuthor): string {
  return `${u.first_name} ${u.last_name}`.trim() || 'Member'
}

function monogram(u: FounderAskAuthor): string {
  return `${u.first_name?.[0] ?? ''}${u.last_name?.[0] ?? ''}`.toUpperCase() || 'M'
}

function PaneAvatar({
  user, size, dimmed = false, dashed = false,
}: { user: FounderAskAuthor; size: number; dimmed?: boolean; dashed?: boolean }) {
  return (
    <span
      className={`inline-flex rounded-full flex-shrink-0 ${dashed ? 'border border-dashed border-gray-400 p-[2px]' : ''} ${dimmed ? 'opacity-60' : ''}`}
    >
      <Avatar style={{ width: size, height: size }}>
        {user.avatar_url && <AvatarImage src={user.avatar_url} alt="" className="object-cover" />}
        <AvatarFallback
          className="text-gray-500 bg-gray-100 font-semibold"
          style={{ fontSize: Math.max(10, Math.round(size * 0.34)) }}
        >
          {monogram(user)}
        </AvatarFallback>
      </Avatar>
    </span>
  )
}

const rowInteractionClasses =
  'w-full text-left rounded-lg transition-colors hover:bg-black/[0.04] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#57241F]/40'

function SkeletonPane() {
  return (
    <div className="p-6 space-y-5 animate-pulse" aria-label="Loading ask context">
      <div className="h-3 w-24 bg-gray-200 rounded" />
      <div className="space-y-2">
        <div className="h-5 w-4/5 bg-gray-200 rounded" />
        <div className="h-4 w-3/5 bg-gray-100 rounded" />
      </div>
      {[0, 1, 2, 3].map(i => (
        <div key={i} className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-gray-200 flex-shrink-0" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3.5 w-1/2 bg-gray-200 rounded" />
            <div className="h-3 w-1/3 bg-gray-100 rounded" />
          </div>
        </div>
      ))}
    </div>
  )
}

interface FounderAskContextPanelProps {
  askId: string
  // Scrolls the centre column to a founder's first reply and highlights it.
  onFounderSelect?: (replyId: string) => void
}

// Fetching wrapper: loads the thread + founders, renders the pane view.
export function FounderAskContextPanel({ askId, onFounderSelect }: FounderAskContextPanelProps) {
  const [askThread, setAskThread] = useState<FounderAskThread | null>(null)
  const [founders, setFounders] = useState<FounderAskAuthor[] | null>(null)

  useEffect(() => {
    let mounted = true
    Promise.all([founderAsksService.fetchAskThread(askId), founderAsksService.fetchFounders()])
      .then(([thread, founderUsers]) => {
        if (!mounted) return
        setAskThread(thread)
        setFounders(founderUsers)
      })
      .catch(err => console.error('Failed to load ask context:', err))
    return () => { mounted = false }
  }, [askId])

  return <FounderAskContextPaneView askThread={askThread} founders={founders} onFounderSelect={onFounderSelect} />
}

interface FounderAskContextPaneViewProps {
  askThread: FounderAskThread | null
  founders: FounderAskAuthor[] | null
  onFounderSelect?: (replyId: string) => void
}

// Right-rail context for a founder ask. Fixed header (ask + asker + links) and
// anchored founders block; the participants list is the pane's only growing
// scroll region, so pane height and founders position are independent of
// thread size. Density is driven by the thresholds in contextPaneHelpers.
// Presentational — exported for the dev demo harness and tests.
export function FounderAskContextPaneView({ askThread, founders, onFounderSelect }: FounderAskContextPaneViewProps) {
  const navigate = useNavigate()
  const [participantsOverflow, setParticipantsOverflow] = useState(false)
  const participantsRef = useRef<HTMLDivElement>(null)

  // Existing convention: profiles are keyed by username; no-op when absent.
  const goToProfile = useCallback((u: FounderAskAuthor) => {
    if (u.username) navigate(`/profile/${u.username}`)
  }, [navigate])

  const founderRows: FounderRow[] = useMemo(
    () => (askThread && founders ? buildFounderRows(founders, askThread.replies) : []),
    [askThread, founders]
  )
  const participantRows: ParticipantRow[] = useMemo(
    () => (askThread ? buildParticipantRows(askThread.ask, askThread.replies) : []),
    [askThread]
  )
  const mode = paneMode(founderRows.length, participantRows.length)

  // The footer appears only when the participants region actually overflows.
  useEffect(() => {
    const el = participantsRef.current
    if (!el) { setParticipantsOverflow(false); return }
    const measure = () => setParticipantsOverflow(el.scrollHeight > el.clientHeight + 1)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [participantRows.length, mode.denseParticipants, askThread])

  const scrollParticipantsToTop = useCallback(() => {
    participantsRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
    participantsRef.current?.focus()
  }, [])

  if (!askThread || !founders) {
    return (
      <div className="h-full border-l border-gray-200/60 bg-white overflow-hidden">
        <SkeletonPane />
      </div>
    )
  }

  const { ask } = askThread
  const hasReplies = askThread.replies.length > 0
  const founderAvatarSize = mode.compactFounders ? 38 : 46
  const showFooter = mode.denseParticipants && participantsOverflow

  return (
    <div
      aria-label="Ask context"
      className="h-full border-l border-gray-200/60 bg-white flex flex-col overflow-hidden"
    >
      {/* Fixed header: the ask, then the asker */}
      <div className="px-6 pt-6 pb-4 flex-shrink-0" aria-label="The ask">
        <p className="text-[11px] font-bold uppercase tracking-widest" style={{ color: MAROON }}>
          Founder Ask
        </p>
        <h3 className="text-lg font-medium text-gray-900 mt-2 break-words">{ask.question}</h3>
        {/* Crowded panes hide the body — the full text stays in the centre column */}
        {ask.detail && !mode.compactAsk && (
          <p className="text-sm text-gray-600 mt-2 whitespace-pre-wrap break-words line-clamp-4">
            {ask.detail}
          </p>
        )}

        {/* Asker: full row normally, one line when crowded */}
        {mode.compactAsk ? (
          <button
            onClick={() => ask.author && goToProfile(ask.author)}
            title={ask.author?.username ? 'View profile' : undefined}
            className={`flex items-center gap-2 mt-3 max-w-full ${rowInteractionClasses}`}
            style={{ width: 'auto' }}
          >
            <PaneAvatar user={ask.author ?? { id: ask.user_id, first_name: 'Member', last_name: '', username: null, avatar_url: null }} size={22} />
            <p className="text-xs text-gray-600 truncate">
              <span className="font-medium text-gray-800">{ask.author ? displayName(ask.author) : 'Member'}</span>
              <span className="text-gray-400"> · asked {formatDistanceToNow(new Date(ask.created_at), { addSuffix: true })}</span>
            </p>
          </button>
        ) : (
          <button
            onClick={() => ask.author && goToProfile(ask.author)}
            title={ask.author?.username ? 'View profile' : undefined}
            className={`flex items-center gap-2.5 mt-4 max-w-full ${rowInteractionClasses}`}
            style={{ width: 'auto' }}
          >
            <PaneAvatar user={ask.author ?? { id: ask.user_id, first_name: 'Member', last_name: '', username: null, avatar_url: null }} size={36} />
            <div className="min-w-0 text-left">
              <p className="text-sm font-medium text-gray-800 truncate">
                {ask.author ? displayName(ask.author) : 'Member'}
              </p>
              <p className="text-[11px] text-gray-400">
                Asked {formatDistanceToNow(new Date(ask.created_at), { addSuffix: true })}
              </p>
            </div>
          </button>
        )}

        {/* Linked content, compact chips (only surface for ask attachments) */}
        {ask.attachments.length > 0 && (
          <div className="flex items-center gap-2 mt-3 overflow-hidden" aria-label="Linked in this ask">
            {ask.attachments.map(att => (
              <button
                key={`${att.type}-${att.id}`}
                onClick={() => {
                  if (att.type === 'contribution') navigate(`/product/${att.id}`)
                  else navigate('/curations')
                }}
                className={`flex items-center gap-1.5 pl-1 pr-2 py-1 rounded-full border border-gray-200/80 min-w-0 ${rowInteractionClasses}`}
                style={{ width: 'auto' }}
              >
                {att.imageUrl ? (
                  <img src={att.imageUrl} alt="" className="h-5 w-5 rounded-full object-cover flex-shrink-0" />
                ) : (
                  <LayoutGrid className="h-4 w-4 text-gray-300 flex-shrink-0" />
                )}
                <span className="text-[11px] text-gray-600 truncate max-w-[120px]">{att.title ?? 'Untitled'}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Founders — anchored; never scrolls with participants */}
      <div
        aria-label="Founders"
        className="flex-shrink-0 px-3 py-3"
        style={{ backgroundColor: FOUNDERS_TINT, borderTop: `1px solid ${HAIRLINE}`, borderBottom: `1px solid ${HAIRLINE}` }}
      >
        <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 px-3 mb-1">
          Founders
        </p>
        <div
          className={mode.foundersScroll ? 'overflow-y-auto max-h-[240px]' : ''}
          role="list"
        >
          {founderRows.map(row => (
            <div
              key={row.user.id}
              role="listitem"
              className={`flex items-center gap-2.5 px-3 ${mode.compactFounders ? 'py-1.5' : 'py-2'} rounded-lg transition-colors hover:bg-black/[0.04]`}
            >
              <button
                onClick={() => goToProfile(row.user)}
                title={row.user.username ? 'View profile' : undefined}
                className="flex-shrink-0 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#57241F]/40"
              >
                <PaneAvatar user={row.user} size={founderAvatarSize} dimmed={!row.hasReplied} dashed={!row.hasReplied} />
              </button>
              <span className="min-w-0 flex-1">
                <button
                  onClick={() => goToProfile(row.user)}
                  title={row.user.username ? 'View profile' : undefined}
                  className="flex items-center gap-1.5 max-w-full text-left rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-[#57241F]/40 hover:underline"
                >
                  <span className={`text-sm font-semibold truncate ${row.hasReplied ? 'text-gray-800' : 'text-gray-400'}`}>
                    {displayName(row.user)}
                  </span>
                  {!mode.compactFounders && (
                    <span
                      className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest rounded-full text-white flex-shrink-0"
                      style={{ backgroundColor: MAROON, opacity: row.hasReplied ? 1 : 0.45 }}
                    >
                      Founder
                    </span>
                  )}
                </button>
                {row.hasReplied ? (
                  <button
                    onClick={() => row.firstReplyId && onFounderSelect?.(row.firstReplyId)}
                    title="Jump to their first reply"
                    className="block text-[11px] text-gray-500 hover:text-[#57241F] hover:underline rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-[#57241F]/40"
                  >
                    Replied · {row.messageCount} {row.messageCount === 1 ? 'message' : 'messages'}
                  </button>
                ) : (
                  <span className="block text-[11px] text-gray-400">Awaiting reply</span>
                )}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Participants — the pane's only growing scroll region. Hidden entirely
          for founder-only threads. */}
      {participantRows.length > 0 && (
        <>
          <div
            ref={participantsRef}
            tabIndex={-1}
            aria-label="Participants in this thread"
            className="flex-1 min-h-0 overflow-y-auto px-3 py-3 focus:outline-none"
            role="list"
          >
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 px-3 mb-1">
              In this thread
            </p>
            {participantRows.map((row, i) => (
              <button
                key={row.user.id}
                role="listitem"
                onClick={() => goToProfile(row.user)}
                title={row.user.username ? 'View profile' : undefined}
                className={`flex items-center gap-2.5 px-3 ${mode.denseParticipants ? 'py-1' : 'py-2'} ${rowInteractionClasses}`}
                style={
                  participantRows.length > VIRTUALIZE_AFTER
                    ? { contentVisibility: 'auto', containIntrinsicSize: '0 40px' }
                    : undefined
                }
              >
                <PaneAvatar user={row.user} size={mode.denseParticipants ? 28 : 34} />
                <span className="min-w-0 flex-1 text-sm text-gray-800 truncate">
                  {displayName(row.user)}
                  {row.isAsker && <span className="text-gray-400"> · asked</span>}
                </span>
                <span className="text-[11px] text-gray-400 flex-shrink-0 tabular-nums">
                  {mode.denseParticipants
                    ? row.messageCount || ''
                    : row.messageCount > 0
                      ? `${row.messageCount} ${row.messageCount === 1 ? 'message' : 'messages'}`
                      : ''}
                </span>
              </button>
            ))}
            {!hasReplies && (
              <p className="text-xs text-gray-400 px-3 py-2">No replies yet.</p>
            )}
          </div>

          {/* Pinned footer — only when the region overflows */}
          {showFooter && (
            <div
              className="flex-shrink-0 flex items-center gap-3 px-6 py-3"
              style={{ borderTop: `1px solid ${HAIRLINE}` }}
            >
              <span className="flex -space-x-2">
                {participantRows.slice(0, 4).map(row => (
                  <span key={row.user.id} className="rounded-full ring-2 ring-white inline-flex">
                    <PaneAvatar user={row.user} size={24} />
                  </span>
                ))}
              </span>
              <button
                onClick={scrollParticipantsToTop}
                className="text-xs font-medium hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#57241F]/40 rounded"
                style={{ color: MAROON }}
              >
                See all {participantRows.length} participants
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
